import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ContourProperties, Form, FormType, Properties } from '../models/properties.model';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { ReactiveFormsModule } from '@angular/forms';
import { SliderModule } from 'primeng/slider';
import { ColorPickerModule } from 'primeng/colorpicker';
import { Effects, Layer, LayerList, LayerType } from '../models/layer.model';
import { effectPropertiesMapping, layerPropertyMapping } from '../models/layer-properties.models/layer.properties';
import { getFieldFromLayer, updateLayerFromFormControl } from '../utils/properties.utils';
import { MonoTypeOperatorFunction, Subject, debounceTime, filter, throttleTime } from 'rxjs';
import { MessageService } from 'primeng/api';
import { MultiSelectModule } from 'primeng/multiselect';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { DropdownModule } from 'primeng/dropdown';
import { ListboxChangeEvent, ListboxModule } from 'primeng/listbox';
import { GradientPickerComponent } from '../input-components/gradient-picker/gradient-picker.component';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ImageDrawService } from '../../services/image-draw.service';
import { TooltipModule } from 'primeng/tooltip';
import { CommonModule } from '@angular/common';


@Component({
  selector: 'app-properties-list',
  standalone: true,
  imports: [
    FloatLabelModule,
    InputTextModule,
    ReactiveFormsModule,
    SliderModule,
    ColorPickerModule,
    MultiSelectModule,
    CheckboxModule,
    InputNumberModule,
    DropdownModule,
    GradientPickerComponent,
    ListboxModule,
    FormsModule,
    ButtonModule,
    TooltipModule,
    CommonModule
  ],
  templateUrl: './properties-list.component.html',
  styleUrl: './properties-list.component.scss'
})
export class PropertiesListComponent implements OnChanges {
  properties: Properties | undefined
  @Input() selectedEffectId: string | undefined | null
  @Input() effectsList: Effects[] | undefined | null
  @Input() layers: LayerList | undefined | null
  @Input() selectedLayerId: string | undefined | null
  @Input() selectedEffect: string | undefined | null
  @Output() updateLayer = new EventEmitter<{layerId: string, layer: Layer}>()
  @Output() selectEffectId = new EventEmitter<string | undefined>()

  formSelectionChanged$ = new Subject<void>()

  private _lastSelectedLayerId: string | undefined
  private _lastSelectedEffectId: string | null | undefined

  form: FormGroup
  formTypes = FormType
  currentLayer: Layer | undefined

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private imageDrawService: ImageDrawService
  ){}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.layers && this.selectedLayerId){
      if (this.skipThisRenderCycle()){
        return
      }
      this.formSelectionChanged$.next()
      this._lastSelectedLayerId = this.selectedLayerId
      this._lastSelectedEffectId = this.selectedEffectId
      this.currentLayer = this.layers[this.selectedLayerId]
      const effectObject = this.layers[this.selectedLayerId].effects.find(e => e.id === this.selectedEffectId)
      this.properties = this.selectedEffectId ?
        effectPropertiesMapping[(effectObject as Effects).effectType] :
        layerPropertyMapping[this.layers[this.selectedLayerId].layerType]
      this.form = this.buildForm(this.properties, this.currentLayer)
      this.buildDefaultBindings(this.form)

      this.form.valueChanges.pipe(
        this.determineCalculationOverloadStrategy(this.layers[this.selectedLayerId])
      ).subscribe((value) => {
        if (!this.layers || !this.selectedLayerId){
          return
        }
        const layer = this.layers[this.selectedLayerId]

        const updatedLayer = updateLayerFromFormControl(layer, value)
        if (this.form.valid) {
          this.updateLayer.emit({layerId: this.selectedLayerId, layer: updatedLayer})
        }

        // Something about the rangeslider, in the angular form, upon updating any other value, changes the output handle position array to READONLY
        // throwing an error and preventing the slider from updating. This is a hacky fix to unfreeze the visible range slide
        if (layer.layerType === LayerType.Contour){
          this.unFreezeVisibleRange()
        }
      })

      this.form.statusChanges.pipe(
        filter(status => status === 'INVALID'),
        debounceTime(1500)
      ).subscribe(_ => {
        if (!this.form.valid) {
          this.messageService.add({severity: 'error', summary: 'Invalid Input', detail: `On: ${this.currentLayer?.name} --> ${this.getInvalidControl()}`});
          console.error(this.form.get(this.getInvalidControl() as string)?.value)
        }
      })
    }
    else {
      this.properties = undefined
      this.currentLayer = undefined
    }
    if (this.selectedLayerId !== this._lastSelectedLayerId){
      this._lastSelectedLayerId = this.selectedLayerId || undefined
    }
    if (this.selectedEffectId !== this._lastSelectedEffectId){
      this._lastSelectedEffectId = this.selectedEffectId || undefined
    }
  }

  unFreezeVisibleRange() {
    const formValue = this.form.get(ContourProperties.ContourVisibleRange)?.value
    const isFrozenForm = Array.isArray(formValue) && Object.isFrozen(formValue);
    if (isFrozenForm) {
      const currentRangeValue = this.form.get(ContourProperties.ContourVisibleRange)
      currentRangeValue?.setValue([...currentRangeValue.getRawValue()], {emitEvent: false})
    }
  }

  getInvalidControl(){
    for (const key in this.form.controls) {
      if (this.form.controls[key].invalid) {
        return this.properties?.forms.find(form => form.id === key)?.label
      }
    }
    return undefined
  }

  private determineCalculationOverloadStrategy(layer: Layer): MonoTypeOperatorFunction<any> {
    if (
      layer.layerType === LayerType.Contour ||
      layer.layerType === LayerType.Roads
    ) {
      return debounceTime(200)
    }
    else {
      return throttleTime(100)
    }
  }

  skipThisRenderCycle(): boolean {
    if (this._lastSelectedLayerId === this.selectedLayerId && this._lastSelectedEffectId === this.selectedEffectId){
      return true
    }
    return false
  }

  buildForm(props: Properties, currentLayer: Layer): FormGroup {
    const group: any = {}
    const buildFormHelper = (field: Form): void => {
      if (field.type === FormType.Color) {
        group[field.id + '_text'] = this.fb.control(
          getFieldFromLayer(currentLayer, field.id) || field.defaultValue || '',
          field.validators || [Validators.required]
        )
      }
      if (field.type === FormType.RangeSlider){
        const initalValue = getFieldFromLayer(currentLayer, field.id)
        const isFrozen = Array.isArray(initalValue) && Object.isFrozen(initalValue);
      }
      if ( field.type === FormType.Container ) {
        field.children?.forEach(buildFormHelper)
        return
      }
      group[field.id] = this.fb.control(
        getFieldFromLayer(currentLayer, field.id) ?? field.defaultValue ?? '',
        field.validators || []
      )
    }
    props.forms.forEach(buildFormHelper)
    const formGroup = this.fb.group(group)
    props.changeHandler(formGroup, this.formSelectionChanged$)
    if (props.onInit) {
      props.onInit({stringEncodings: this.imageDrawService.getCurrentStringEncodings()})
    }
    return formGroup
  }

  buildDefaultBindings(formGroup: FormGroup): void{
    if (!this.properties) return
    this.properties.forms.forEach(field => {
      if (field.type === FormType.Color) {
        formGroup.get(field.id + '_text')?.valueChanges.subscribe((value) => {
          formGroup.get(field.id)?.setValue(value, {emitEvent: false})
        })
        formGroup.get(field.id)?.valueChanges.subscribe((value) => {
          formGroup.get(field.id + '_text')?.setValue(value, {emitEvent: false})
        })
      }
      else if (field.type === FormType.Gradient){
      }
    })
  }

  onSelectedEffect(effect: ListboxChangeEvent): void{
    if (typeof effect.value === 'string') {
      this.selectEffectId.emit(effect.value)
      return
    }
    if (effect.value === null) {
      this.selectEffectId.emit(undefined)
      return
    }
    this.selectEffectId.emit(effect.value.id)
  }
}
